const FULL_ANGLE = 2 * Math.PI;

const drawRoundObject = (position, radius, graph) => {
    graph.beginPath();
    graph.arc(position.x, position.y, radius, 0, FULL_ANGLE);
    graph.closePath();
    graph.fill();
    graph.stroke();
};

export const drawFood = (position, food, graph) => {
    // Colorful food pellets
    graph.fillStyle = 'hsl(' + food.hue + ', 100%, 50%)';
    graph.strokeStyle = 'hsl(' + food.hue + ', 100%, 40%)';
    graph.lineWidth = 1;
    drawRoundObject(position, food.radius, graph);
};

export const drawVirus = (position, virus, graph) => {
    graph.strokeStyle = virus.stroke;
    graph.fillStyle = virus.fill;
    graph.lineWidth = virus.strokeWidth;
    let sides = 20;

    graph.beginPath();
    for (let theta = 0; theta < FULL_ANGLE; theta += FULL_ANGLE / sides) {
        let point = circlePoint(position, virus.radius, theta);
        graph.lineTo(point.x, point.y);
    }
    graph.closePath();
    graph.stroke();
    graph.fill();
};

export const drawFireFood = (position, mass, playerConfig, graph) => {
    graph.strokeStyle = 'hsl(' + mass.hue + ', 100%, 45%)';
    graph.fillStyle = 'hsl(' + mass.hue + ', 100%, 50%)';
    graph.lineWidth = playerConfig.border + 2;
    drawRoundObject(position, mass.radius - 1, graph);
};

const valueInRange = (min, max, value) => Math.min(max, Math.max(min, value));

const circlePoint = (origo, radius, theta) => ({
    x: origo.x + radius * Math.cos(theta),
    y: origo.y + radius * Math.sin(theta)
});

const cellTouchingBorders = (cell, borders) =>
    cell.x - cell.radius <= borders.left ||
    cell.x + cell.radius >= borders.right ||
    cell.y - cell.radius <= borders.top ||
    cell.y + cell.radius >= borders.bottom;

const regulatePoint = (point, borders) => ({
    x: valueInRange(borders.left, borders.right, point.x),
    y: valueInRange(borders.top, borders.bottom, point.y)
});

const drawCellWithLines = (cell, borders, graph) => {
    let pointCount = 30 + ~~(cell.mass / 5);
    let points = [];
    for (let theta = 0; theta < FULL_ANGLE; theta += FULL_ANGLE / pointCount) {
        let point = circlePoint(cell, cell.radius, theta);
        points.push(regulatePoint(point, borders));
    }
    graph.beginPath();
    graph.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
        graph.lineTo(points[i].x, points[i].y);
    }
    graph.closePath();
    graph.fill();
    graph.stroke();
};

export const drawCells = (cells, playerConfig, toggleMassState, borders, graph) => {
    for (let cell of cells) {
        // Check if player is frozen (cashing out)
        const isFrozen = cell.isFrozen || false;
        
        // Frozen players have a blue/ice tint
        if (isFrozen) {
            graph.fillStyle = 'rgba(100, 180, 255, 0.8)';
            graph.strokeStyle = 'rgba(50, 130, 200, 1)';
        } else {
            // Make all players green
            graph.fillStyle = '#2ecc71';
            graph.strokeStyle = '#27ae60';
        }
        
        graph.lineWidth = 6;
        if (cellTouchingBorders(cell, borders)) {
            drawCellWithLines(cell, borders, graph);
        } else {
            drawRoundObject(cell, cell.radius, graph);
        }

        // Draw frozen indicator (ice crystals effect)
        if (isFrozen) {
            drawFrozenEffect(cell, graph);
        }

        let fontSize = Math.max(cell.radius / 3, 12);
        graph.lineWidth = playerConfig.textBorderSize;
        graph.fillStyle = playerConfig.textColor;
        graph.strokeStyle = playerConfig.textBorder;
        graph.miterLimit = 1;
        graph.lineJoin = 'round';
        graph.textAlign = 'center';
        graph.textBaseline = 'middle';
        graph.font = 'bold ' + fontSize + 'px sans-serif';
        graph.strokeText(cell.name, cell.x, cell.y);
        graph.fillText(cell.name, cell.x, cell.y);

        // Show cash out timer for frozen players
        if (isFrozen && cell.cashOutTimeRemaining > 0) {
            graph.font = 'bold ' + Math.max(fontSize / 2, 14) + 'px sans-serif';
            graph.fillStyle = '#FFD700';
            graph.strokeStyle = '#000000';
            graph.strokeText('💰 ' + cell.cashOutTimeRemaining + 's', cell.x, cell.y - fontSize - 10);
            graph.fillText('💰 ' + cell.cashOutTimeRemaining + 's', cell.x, cell.y - fontSize - 10);
        } else if (toggleMassState === 1) {
            graph.font = 'bold ' + Math.max(fontSize / 3 * 2, 10) + 'px sans-serif';
            if (cell.name.length === 0) fontSize = 0;
            graph.strokeText(Math.round(cell.mass), cell.x, cell.y + fontSize);
            graph.fillText(Math.round(cell.mass), cell.x, cell.y + fontSize);
        }
    }
};

// Draw frozen/ice effect around a cell
const drawFrozenEffect = (cell, graph) => {
    const numCrystals = 8;
    graph.strokeStyle = 'rgba(200, 230, 255, 0.8)';
    graph.lineWidth = 2;
    
    for (let i = 0; i < numCrystals; i++) {
        const angle = (i / numCrystals) * FULL_ANGLE;
        const innerRadius = cell.radius * 0.9;
        const outerRadius = cell.radius * 1.15;
        
        const x1 = cell.x + Math.cos(angle) * innerRadius;
        const y1 = cell.y + Math.sin(angle) * innerRadius;
        const x2 = cell.x + Math.cos(angle) * outerRadius;
        const y2 = cell.y + Math.sin(angle) * outerRadius;
        
        graph.beginPath();
        graph.moveTo(x1, y1);
        graph.lineTo(x2, y2);
        graph.stroke();
    }
};

export const drawGrid = (settings, player, screen, graph) => {
    graph.lineWidth = 1;
    graph.strokeStyle = '#E0E0E0';
    graph.globalAlpha = 1;
    graph.beginPath();

    const gridSize = 50;
    const offsetX = -player.x % gridSize;
    const offsetY = -player.y % gridSize;

    for (let x = offsetX; x < screen.width; x += gridSize) {
        graph.moveTo(x, 0);
        graph.lineTo(x, screen.height);
    }

    for (let y = offsetY; y < screen.height; y += gridSize) {
        graph.moveTo(0, y);
        graph.lineTo(screen.width, y);
    }

    graph.stroke();
};

export const drawBorder = (borders, graph) => {
    graph.lineWidth = 1;
    graph.strokeStyle = '#000000';
    graph.beginPath();
    graph.moveTo(borders.left, borders.top);
    graph.lineTo(borders.right, borders.top);
    graph.lineTo(borders.right, borders.bottom);
    graph.lineTo(borders.left, borders.bottom);
    graph.closePath();
    graph.stroke();
};

export const drawErrorMessage = (message, graph, screen) => {
    graph.fillStyle = '#333333';
    graph.fillRect(0, 0, screen.width, screen.height);
    graph.textAlign = 'center';
    graph.fillStyle = '#FFFFFF';
    graph.font = 'bold 30px sans-serif';
    graph.fillText(message, screen.width / 2, screen.height / 2);
};

export const getPosition = (entity, player, screen) => {
    return {
        x: entity.x - player.x + screen.width / 2,
        y: entity.y - player.y + screen.height / 2
    };
};


